import React from "react";
import { getImageUrl } from "@/lib/media";
import { useCheckoutContext } from "../CheckoutContext";
import { PromotionalCode } from "./PromotionalCode";
import { CheckoutStepIntro } from "./CheckoutStepIntro";

interface CheckoutPackageStepProps {
  showIntro?: boolean;
}

export const CheckoutPackageStep: React.FC<CheckoutPackageStepProps> = ({
  showIntro = true,
}) => {
  const { packageInfo, getFinalPrice, formatCurrency, formatPercentage } = useCheckoutContext();

  if (!packageInfo) {
    return <p className="text-text-secondary">Carregando pacote...</p>;
  }

  const subtotal = packageInfo.price ?? 0;
  const finalPrice = getFinalPrice();
  const hasDiscount = finalPrice < subtotal;

  return (
    <div className="flex flex-col gap-8">
      {showIntro ? (
        <CheckoutStepIntro
          title="Confirme seu pacote"
          description="Revise nome, conteúdo e valor antes de seguir para os seus dados."
        />
      ) : null}

      <article className="flex flex-col overflow-hidden rounded-xl border border bg-background sm:flex-row">
        {packageInfo.imageUrl ? (
          <img
            src={getImageUrl(packageInfo.imageUrl)}
            alt={packageInfo.name || "Imagem do pacote"}
            className="sm:w-40 h-32 sm:h-auto w-full object-cover shrink-0 bg-gradient-to-br from-emerald-700 to-teal-600"
          />
        ) : (
          <div className="sm:w-40 h-32 sm:min-h-[140px] shrink-0 bg-gradient-to-br from-emerald-700 to-teal-600" />
        )}
        <div className="flex flex-1 flex-col justify-center p-5 sm:p-6">
          <h3 className="text-lg font-semibold text-foreground">{packageInfo.name}</h3>
          <p className="mt-1 text-sm text-text-secondary">
            {packageInfo.modulesCount
              ? `${packageInfo.modulesCount} ${packageInfo.modulesCount === 1 ? "módulo" : "módulos"}`
              : null}
            {packageInfo.modality ? ` · ${packageInfo.modality}` : null}
            {packageInfo.level ? ` · ${packageInfo.level}` : null}
          </p>
          <div className="mt-4 flex flex-wrap items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">{formatCurrency(finalPrice)}</span>
            {packageInfo.isRecurring ? (
              <span className="text-sm text-text-secondary">
                {packageInfo.recurrenceIntervalMonths === 1
                  ? "/mês"
                  : packageInfo.recurrenceIntervalMonths === 3
                    ? "/trimestre"
                    : packageInfo.recurrenceIntervalMonths === 6
                      ? "/semestre"
                      : packageInfo.recurrenceIntervalMonths === 12
                        ? "/ano"
                        : packageInfo.recurrenceIntervalMonths
                          ? `/ ${packageInfo.recurrenceIntervalMonths} meses`
                          : "por ciclo"}
              </span>
            ) : null}
            {hasDiscount ? (
              <>
                <span className="text-sm text-text-disabled line-through">{formatCurrency(subtotal)}</span>
                {packageInfo.discount ? (
                  <span className="rounded-full bg-success-muted px-2 py-0.5 text-xs font-medium text-success-subtle">
                    {formatPercentage(packageInfo.discount)} OFF
                  </span>
                ) : null}
              </>
            ) : null}
          </div>
          {packageInfo.isRecurring ? (
            <p className="mt-2 text-sm text-text-secondary">
              Cobrança recorrente no cartão. Cancele quando quiser; o acesso segue até o fim do período pago.
            </p>
          ) : null}
          {packageInfo.description ? (
            <p className="mt-3 text-sm text-text-secondary line-clamp-3">{packageInfo.description}</p>
          ) : null}
        </div>
      </article>

      <PromotionalCode />
    </div>
  );
};
